"use client";

import { useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { useContactFormSubmission } from "@/components/content/hooks";

/**
 * "Sie kennen jemanden?" — collapsible referral form under each Gesuch card.
 *
 * Reuses the /kontakt submission path end-to-end (useContactFormSubmission →
 * POST /api/contact → contact.submitted event): Turnstile bot protection,
 * Teams notification and the triage agent's lead research all come for free.
 * `topic` is built by the (server) card from the OPAQUE reference + region —
 * never the request id, which the public view does not expose — so sales can
 * locate the Gesuch from the Teams card. Receiving only the string keeps the
 * full view row out of this client component's props.
 */
export function GesuchReferralForm({ topic }: { topic: string }) {
  const [open, setOpen] = useState(false);
  const { formData, formStatus, handleChange, handleSubmit } = useContactFormSubmission({
    initialTopic: topic,
  });

  const inputClasses =
    "w-full bg-transparent border-0 border-b border-on-surface-variant/20 focus:border-secondary focus:ring-0 transition-colors pb-2 text-sm placeholder:text-on-surface-variant/40";

  if (formStatus === "success") {
    return (
      <div className="flex items-center gap-2 rounded-xl bg-secondary/10 px-4 py-3 text-sm text-secondary">
        <Check className="h-4 w-4 shrink-0" aria-hidden />
        Danke für Ihren Hinweis — wir melden uns.
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="inline-flex items-center gap-2 text-sm font-medium text-secondary hover:underline"
      >
        Sie kennen jemanden? Hinweis geben
        <ChevronDown
          className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden
        />
      </button>

      {open && (
        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <input
              name="name"
              type="text"
              required
              placeholder="Ihr Name *"
              aria-label="Ihr Name"
              value={formData.name}
              onChange={handleChange}
              className={inputClasses}
              style={{ outline: "none" }}
            />
            <input
              name="email"
              type="email"
              required
              placeholder="Ihre E-Mail *"
              aria-label="Ihre E-Mail"
              value={formData.email}
              onChange={handleChange}
              className={inputClasses}
              style={{ outline: "none" }}
            />
          </div>
          <textarea
            name="message"
            rows={3}
            required
            placeholder="Ihr Hinweis — z. B. wen wir ansprechen dürfen *"
            aria-label="Ihr Hinweis"
            value={formData.message}
            onChange={handleChange}
            className={`${inputClasses} resize-none`}
            style={{ outline: "none" }}
          />
          {formStatus === "error" && (
            <p className="text-sm text-red-600">
              Leider ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut oder schreiben Sie
              uns an info@oekopunktemarkt.de.
            </p>
          )}
          <button
            type="submit"
            disabled={formStatus === "submitting"}
            className="rounded-full border border-primary px-5 py-2 text-sm font-bold text-primary hover:bg-primary/5 disabled:opacity-50"
          >
            {formStatus === "submitting" ? "Wird gesendet…" : "Hinweis senden"}
          </button>
        </form>
      )}
    </div>
  );
}
